import { useState } from 'react';
import { X, Plus, Trash2, GripVertical } from 'lucide-react';
import type { Survey, SurveyQuestion, SurveyTrigger } from '../../api/client';

interface SurveyBuilderProps {
  survey?: Survey | null;
  onClose: () => void;
  onSave: (data: Partial<Survey>) => void;
  isSaving: boolean;
}

export default function SurveyBuilder({ survey, onClose, onSave, isSaving }: SurveyBuilderProps) {
  const [name, setName] = useState(survey?.name || '');
  const [status, setStatus] = useState<Survey['status']>(survey?.status || 'draft');
  const [trigger, setTrigger] = useState<SurveyTrigger>(
    survey?.trigger || { type: 'pageview', value: '/' },
  );
  const [questions, setQuestions] = useState<SurveyQuestion[]>(
    survey?.questions || [
      { id: crypto.randomUUID(), type: 'rating', text: '', required: true },
    ],
  );

  function addQuestion() {
    setQuestions([
      ...questions,
      {
        id: crypto.randomUUID(),
        type: 'rating',
        text: '',
        required: true,
      },
    ]);
  }

  function removeQuestion(id: string) {
    if (questions.length <= 1) return;
    setQuestions(questions.filter((q) => q.id !== id));
  }

  function updateQuestion(id: string, updates: Partial<SurveyQuestion>) {
    setQuestions(questions.map((q) => (q.id === id ? { ...q, ...updates } : q)));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ name, status, trigger, questions });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8">
      <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {survey ? 'Edit Survey' : 'Create Survey'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-5">
          {/* Name & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Survey Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input mt-1"
                placeholder="e.g. User Satisfaction"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Survey['status'])}
                className="input mt-1"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          {/* Trigger */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Trigger</label>
            <div className="grid grid-cols-2 gap-4">
              <select
                value={trigger.type}
                onChange={(e) =>
                  setTrigger({ ...trigger, type: e.target.value as SurveyTrigger['type'] })
                }
                className="input"
              >
                <option value="pageview">Page View</option>
                <option value="exit_intent">Exit Intent</option>
                <option value="time_on_page">Time on Page</option>
                <option value="scroll_depth">Scroll Depth</option>
              </select>
              <input
                type="text"
                value={trigger.value}
                onChange={(e) => setTrigger({ ...trigger, value: e.target.value })}
                className="input"
                placeholder={
                  trigger.type === 'time_on_page'
                    ? 'Seconds (e.g. 30)'
                    : trigger.type === 'scroll_depth'
                      ? 'Percentage (e.g. 50)'
                      : 'Page URL'
                }
              />
            </div>
            {(trigger.type === 'pageview' || trigger.type === 'exit_intent') && (
              <input
                type="text"
                value={trigger.url || ''}
                onChange={(e) => setTrigger({ ...trigger, url: e.target.value })}
                className="input mt-2"
                placeholder="Target URL (optional, e.g. /pricing)"
              />
            )}
          </div>

          {/* Questions */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Questions</label>
            <div className="space-y-3">
              {questions.map((q, i) => (
                <div
                  key={q.id}
                  className="rounded-lg border border-gray-200 p-4"
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="mt-2 h-4 w-4 shrink-0 text-gray-300" />
                    <div className="flex-1 space-y-3">
                      <div className="flex gap-2">
                        <span className="mt-2 text-xs font-medium text-gray-400">Q{i + 1}</span>
                        <input
                          type="text"
                          value={q.text}
                          onChange={(e) => updateQuestion(q.id, { text: e.target.value })}
                          className="input flex-1"
                          placeholder="Enter your question"
                          required
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <select
                          value={q.type}
                          onChange={(e) =>
                            updateQuestion(q.id, {
                              type: e.target.value as SurveyQuestion['type'],
                            })
                          }
                          className="rounded border-gray-300 text-sm"
                        >
                          <option value="rating">Rating (1-5)</option>
                          <option value="nps">NPS (0-10)</option>
                          <option value="text">Free Text</option>
                          <option value="multiple_choice">Multiple Choice</option>
                        </select>
                        <label className="flex items-center gap-1.5 text-sm text-gray-500">
                          <input
                            type="checkbox"
                            checked={q.required}
                            onChange={(e) => updateQuestion(q.id, { required: e.target.checked })}
                            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                          Required
                        </label>
                      </div>

                      {q.type === 'multiple_choice' && (
                        <div className="space-y-1">
                          {(q.options || ['']).map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={opt}
                                onChange={(e) => {
                                  const newOptions = [...(q.options || [''])];
                                  newOptions[oi] = e.target.value;
                                  updateQuestion(q.id, { options: newOptions });
                                }}
                                className="input flex-1 text-sm"
                                placeholder={`Option ${oi + 1}`}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newOptions = (q.options || ['']).filter((_, j) => j !== oi);
                                  updateQuestion(q.id, { options: newOptions.length ? newOptions : [''] });
                                }}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() =>
                              updateQuestion(q.id, { options: [...(q.options || ['']), ''] })
                            }
                            className="text-xs text-brand-600 hover:text-brand-700"
                          >
                            + Add option
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeQuestion(q.id)}
                      disabled={questions.length <= 1}
                      className="mt-2 text-gray-400 hover:text-red-500 disabled:opacity-30"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addQuestion}
              className="mt-2 flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700"
            >
              <Plus className="h-4 w-4" />
              Add Question
            </button>
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSaving} className="btn-primary">
              {isSaving ? 'Saving...' : survey ? 'Update Survey' : 'Create Survey'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
